import type { SmsAgentOpsCounts } from 'app/types/sms-agent-ops'
import { LOW_VOLUME_FLOOR, TAKEOVER_CRITICAL_AT, TAKEOVER_WARNING_AT } from './smsTakeovers'

/** Shared by the KPI card info tooltips and the table header tooltips. */
export const METRIC_DEFINITIONS = {
  inbound: `Texts patients sent to the location's numbers.`,
  outbound: `Every text the location sent: AI agent, workflow, staff, CallRail and voice agent.`,
  agentTexts: `Replies the SMS AI agent wrote in a conversation.`,
  workflowTexts: `Texts a workflow step sent, including the AI agent's opening text.`,
  staffTexts: `Texts staff sent from the inbox.`,
  externalTexts: `Texts sent from CallRail and synced in.`,
  voiceAgentTexts: `Texts the voice agent sent during a call with its send-SMS tool.`,
  aiConversations: `Threads where the AI agent sent at least one text in the date range.`,
  repliedConversations: `AI conversations where the patient texted back after the AI's first text.`,
  replyRate: `Patient replied ÷ AI conversations.`,
  bookings: `Appointments with source "SMS Agent" booked (created) in the date range, whenever the visit is.`,
  bookingRate: `SMS Agent bookings ÷ AI conversations.`,
  takeovers: `AI conversations that left the AI agent: staff replied, staff turned the AI off, or the patient texted STOP. A thread counts once, by its first takeover in range.`,
  takeoverRate: `Takeovers ÷ AI conversations. Rated only at ${LOW_VOLUME_FLOOR} or more AI conversations; ${TAKEOVER_CRITICAL_AT}% or more is critical and ${TAKEOVER_WARNING_AT}% or more is a warning.`,
  previousTakeoverRate: `Takeover rate for the same number of days immediately before the selected range.`,
  takeoverRateChange: `Takeover rate minus the previous period's, in percentage points. Above zero means staff are stepping in more.`,
  staffReplyTakeovers: `Staff sent a text on an AI thread, which turns the AI off.`,
  manualToggleTakeovers: `Staff turned the AI off from the inbox without texting.`,
  optOutTakeovers: `The patient texted STOP, which turns the AI off.`,
  failedTexts: `Outbound texts that failed: undelivered, failed at the carrier, blocked for consent or quiet hours, or stuck pending.`,
  failureRate: `Failed texts ÷ outbound texts.`,
  undeliveredTexts: `The carrier accepted the text but could not deliver it (Twilio "undelivered").`,
  carrierFailedTexts: `The provider rejected the text (status "failed" with a carrier error code).`,
  noConsentTexts: `Blocked before sending: a marketing text to a contact without marketing consent.`,
  quietHoursTexts: `Blocked before sending: it would have gone out during quiet hours.`,
  stuckPendingTexts: `Still "pending" an hour after creation. An immediate send that threw leaves its text here with no error code.`,
  optOuts: `Numbers that opted out in the date range, by STOP, a form, or staff.`,
  inboundThreads: `Threads with at least one patient text in the date range.`,
  unansweredThreads: `Threads where a patient text got no reply of any kind (staff, AI or workflow) by the end of the location's next business day.`,
  unansweredRate: `Unanswered threads ÷ inbound threads.`,
  previousUnansweredRate: `Unanswered rate for the previous period.`,
  unansweredRateChange: `Unanswered rate minus the previous period's, in percentage points. Above zero means more patients are waiting.`,
  aiTurns: `Replies the AI agent generated (AI conversation turns).`,
  toolErrorTurns: `AI replies where a tool the agent called returned an error.`,
  toolErrorRate: `AI replies with a tool error ÷ AI replies.`,
  avgAiLatency: `Average time the LLM took to write an AI reply. Excludes carrier delivery time.`,
  costPerConversation: `LLM plus estimated carrier cost of AI replies ÷ AI conversations.`,
  avgStaffResponse: `Average time from a patient's text to staff's first reply, on threads staff answered.`,
}

export type FunnelStage = { key: string; label: string; count: number; definition: string }

/** AI conversations → patient replied → booked, each as a share of the first stage (plan D15). */
export function aiFunnelStages(counts: SmsAgentOpsCounts): FunnelStage[] {
  return [
    {
      key: `aiConversations`,
      label: `AI Conversations`,
      count: counts.aiConversations,
      definition: METRIC_DEFINITIONS.aiConversations,
    },
    {
      key: `repliedConversations`,
      label: `Patient Replied`,
      count: counts.repliedConversations,
      definition: METRIC_DEFINITIONS.repliedConversations,
    },
    { key: `bookings`, label: `Booked`, count: counts.bookings, definition: METRIC_DEFINITIONS.bookings },
  ]
}
